import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { ApiClientError } from '../../../services/apiClient';
import {
  atualizarRepresentante,
  atualizarVinculo,
  criarRepresentante,
  listarEmpresasAcessiveis,
  listarRepresentantes,
  listarUsuariosDisponiveis,
  listarVinculos,
  vincularUsuario,
} from '../../../services/usuariosEmpresaApi';
import {
  EmpresaRepresentante,
  EmpresaResumo,
  PapelOperacional,
  UsuarioEmpresa,
  UsuarioResumo,
} from '../../../types/usuariosEmpresa';

const papeis: Array<{ value: PapelOperacional; label: string }> = [
  { value: 'gestor', label: 'Gestor' },
  { value: 'operador', label: 'Operador' },
  { value: 'motorista', label: 'Motorista' },
  { value: 'operador_destinador', label: 'Operador destinador' },
  { value: 'auditor', label: 'Auditor' },
];

function errorMessage(error: unknown): string {
  return error instanceof ApiClientError ? error.message : 'Não foi possível concluir a operação.';
}

const UsuariosEmpresaPage: React.FC = () => {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<'usuarios' | 'representantes'>('usuarios');
  const [empresas, setEmpresas] = useState<EmpresaResumo[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioResumo[]>([]);
  const [empresaId, setEmpresaId] = useState('');
  const [vinculos, setVinculos] = useState<UsuarioEmpresa[]>([]);
  const [representantes, setRepresentantes] = useState<EmpresaRepresentante[]>([]);
  const [search, setSearch] = useState('');
  const [papelFilter, setPapelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [papel, setPapel] = useState<PapelOperacional>('operador');
  const [representante, setRepresentante] = useState({ nome: '', email: '', telefone: '', cargo: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadRecords = useCallback(async () => {
    if (!accessToken || !empresaId) return;
    setLoading(true);
    setError(null);
    try {
      if (tab === 'usuarios') {
        const result = await listarVinculos(accessToken, empresaId, {
          search,
          papel: papelFilter,
          status: statusFilter,
        });
        setVinculos(result.items);
      } else {
        const result = await listarRepresentantes(accessToken, empresaId, { search, status: statusFilter });
        setRepresentantes(result.items);
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, empresaId, papelFilter, search, statusFilter, tab]);

  useEffect(() => {
    if (!accessToken) return;
    Promise.all([listarEmpresasAcessiveis(accessToken), listarUsuariosDisponiveis(accessToken)])
      .then(([companyResult, userResult]) => {
        setEmpresas(companyResult.items);
        setUsuarios(userResult.items);
        setEmpresaId((current) => current || companyResult.items[0]?.id || '');
      })
      .catch((requestError: unknown) => setError(errorMessage(requestError)));
  }, [accessToken]);

  useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  const submitLink = async (event: FormEvent) => {
    event.preventDefault();
    if (!accessToken || !empresaId || !usuarioId) return;
    setError(null);
    try {
      await vincularUsuario(accessToken, empresaId, { usuarioId, papelOperacional: papel });
      setUsuarioId('');
      setSuccess('Acesso vinculado à empresa.');
      await loadRecords();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const submitRepresentative = async (event: FormEvent) => {
    event.preventDefault();
    if (!accessToken || !empresaId) return;
    setError(null);
    try {
      await criarRepresentante(accessToken, empresaId, {
        nome: representante.nome,
        email: representante.email || undefined,
        telefone: representante.telefone || undefined,
        cargo: representante.cargo || undefined,
      });
      setRepresentante({ nome: '', email: '', telefone: '', cargo: '' });
      setSuccess('Representante cadastrado.');
      await loadRecords();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const toggleLink = async (item: UsuarioEmpresa) => {
    if (!accessToken) return;
    try {
      await atualizarVinculo(accessToken, empresaId, item.usuarioId, {
        status: item.status === 'ativo' ? 'inativo' : 'ativo',
      });
      await loadRecords();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const toggleRepresentative = async (item: EmpresaRepresentante) => {
    if (!accessToken) return;
    try {
      await atualizarRepresentante(accessToken, empresaId, item.id, {
        status: item.status === 'ativo' ? 'inativo' : 'ativo',
      });
      await loadRecords();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  if (!accessToken) {
    return <div className="rounded-lg bg-amber-50 p-4 text-amber-800">Autentique-se pela API para administrar acessos empresariais.</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Acessos por empresa</h2>
        <p className="mt-1 text-sm text-gray-600">Colaboradores e representantes com escopo validado pelo backend.</p>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700">{success}</div>}

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <label className="block text-sm font-medium text-gray-700">Empresa</label>
        <select className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" value={empresaId} onChange={(event) => setEmpresaId(event.target.value)}>
          {empresas.map((company) => <option key={company.id} value={company.id}>{company.nomeFantasia || company.razaoSocial}</option>)}
        </select>
        {empresas.length === 0 && <p className="mt-3 text-sm text-gray-500">Nenhuma empresa disponível no seu escopo.</p>}
      </section>

      <div className="flex gap-2 border-b border-gray-200">
        {(['usuarios', 'representantes'] as const).map((item) => (
          <button key={item} type="button" onClick={() => setTab(item)} className={`px-4 py-3 text-sm font-medium ${tab === item ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-gray-500'}`}>
            {item === 'usuarios' ? 'Usuários vinculados' : 'Representantes'}
          </button>
        ))}
      </div>

      <section className="grid gap-4 rounded-xl bg-white p-5 shadow-sm md:grid-cols-3">
        <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Buscar nome, e-mail ou cargo" value={search} onChange={(event) => setSearch(event.target.value)} />
        {tab === 'usuarios' && <select className="rounded-lg border border-gray-300 px-3 py-2" value={papelFilter} onChange={(event) => setPapelFilter(event.target.value)}><option value="">Todos os papéis</option>{papeis.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>}
        <select className="rounded-lg border border-gray-300 px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">Todos os status</option><option value="ativo">Ativo</option><option value="inativo">Inativo</option></select>
      </section>

      {tab === 'usuarios' ? (
        <>
          <form onSubmit={submitLink} className="grid gap-3 rounded-xl bg-white p-5 shadow-sm md:grid-cols-[1fr_220px_auto]">
            <select required className="rounded-lg border border-gray-300 px-3 py-2" value={usuarioId} onChange={(event) => setUsuarioId(event.target.value)}><option value="">Selecione um usuário do tenant</option>{usuarios.map((item) => <option key={item.id} value={item.id}>{item.nome} — {item.email}</option>)}</select>
            <select className="rounded-lg border border-gray-300 px-3 py-2" value={papel} onChange={(event) => setPapel(event.target.value as PapelOperacional)}>{papeis.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
            <button className="rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700" type="submit">Vincular</button>
          </form>
          <div className="overflow-hidden rounded-xl bg-white shadow-sm"><table className="w-full text-left text-sm"><thead className="bg-gray-50 text-gray-600"><tr><th className="px-4 py-3">Usuário</th><th className="px-4 py-3">Papel</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Ação</th></tr></thead><tbody>{vinculos.map((item) => <tr key={item.id} className="border-t"><td className="px-4 py-3"><div className="font-medium">{item.usuario.nome}</div><div className="text-gray-500">{item.usuario.email}</div></td><td className="px-4 py-3">{papeis.find((role) => role.value === item.papelOperacional)?.label}</td><td className="px-4 py-3">{item.status}</td><td className="px-4 py-3"><button type="button" onClick={() => void toggleLink(item)} className="text-emerald-700 hover:underline">{item.status === 'ativo' ? 'Desativar' : 'Ativar'}</button></td></tr>)}</tbody></table></div>
        </>
      ) : (
        <>
          <form onSubmit={submitRepresentative} className="grid gap-3 rounded-xl bg-white p-5 shadow-sm md:grid-cols-2 lg:grid-cols-5">
            <input required minLength={2} className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Nome" value={representante.nome} onChange={(event) => setRepresentante((value) => ({ ...value, nome: event.target.value }))} />
            <input type="email" className="rounded-lg border border-gray-300 px-3 py-2" placeholder="E-mail operacional" value={representante.email} onChange={(event) => setRepresentante((value) => ({ ...value, email: event.target.value }))} />
            <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Telefone" value={representante.telefone} onChange={(event) => setRepresentante((value) => ({ ...value, telefone: event.target.value }))} />
            <input className="rounded-lg border border-gray-300 px-3 py-2" placeholder="Cargo" value={representante.cargo} onChange={(event) => setRepresentante((value) => ({ ...value, cargo: event.target.value }))} />
            <button className="rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700" type="submit">Cadastrar</button>
          </form>
          <div className="overflow-hidden rounded-xl bg-white shadow-sm"><table className="w-full text-left text-sm"><thead className="bg-gray-50 text-gray-600"><tr><th className="px-4 py-3">Representante</th><th className="px-4 py-3">Contato</th><th className="px-4 py-3">Cargo</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Ação</th></tr></thead><tbody>{representantes.map((item) => <tr key={item.id} className="border-t"><td className="px-4 py-3 font-medium">{item.nome}</td><td className="px-4 py-3">{item.email || item.telefone}</td><td className="px-4 py-3">{item.cargo || '—'}</td><td className="px-4 py-3">{item.status}</td><td className="px-4 py-3"><button type="button" onClick={() => void toggleRepresentative(item)} className="text-emerald-700 hover:underline">{item.status === 'ativo' ? 'Desativar' : 'Ativar'}</button></td></tr>)}</tbody></table></div>
        </>
      )}
      {loading && <p className="text-sm text-gray-500">Carregando...</p>}
    </div>
  );
};

export default UsuariosEmpresaPage;
